import { OrgChartEmployee } from '../types/orgChart.types';
import { buildFolderStructure } from './pdfGenerator';
import { getCurrentQuarter } from './currentQuarter';

/**
 * Suffix appended to a leader's alias to name their root WorkDocs folder,
 * e.g. `jdoe` -> `jdoe-baseball-cards`.
 */
export const ROOT_FOLDER_SUFFIX = '-baseball-cards';

/**
 * The three fixed folders created directly under the root, in display order.
 * `Supplemental Context` is the parent of the recursive per-person org tree;
 * `Generated Baseball Cards` receives the PDFs produced by the bulk generate flow.
 */
export const FIXED_SUBFOLDERS = ['Manager Hub Exports', 'Supplemental Context', 'Generated Baseball Cards'] as const;

/** Name of the fixed subfolder that holds the recursive org tree. Must be one of
 * FIXED_SUBFOLDERS — the provisioning hook looks the org-tree parent up by this name. */
export const SUPPLEMENTAL_DATA_FOLDER = 'Supplemental Context';

/** Name of the fixed subfolder that holds the Manager Hub ratings export (one team-wide xlsx).
 * Must be one of FIXED_SUBFOLDERS — the Step 4 folder checks look this folder up by name. */
export const MANAGER_HUB_EXPORTS_FOLDER = 'Manager Hub Exports';

/** Name of the fixed subfolder that generated baseball-card PDFs are uploaded into.
 * Must be one of FIXED_SUBFOLDERS — the generate flow finds-or-creates it by this name. */
export const GENERATED_CARDS_FOLDER = 'Generated Baseball Cards';

/**
 * Builds the quarter-scoped root folder name for a leader, e.g. `mkteja-baseball-cards-q3-2026`.
 * The alias is lowercased and the current calendar quarter+year are appended so cards can be run
 * quarter by quarter — each quarter gets its own root, and re-running within the same quarter
 * resolves to the same name (find-or-create stays idempotent). The quarter comes from the shared
 * {@link getCurrentQuarter} so it can never disagree with the PDF's `talentCardPeriod`.
 */
export const buildRootFolderName = (alias: string): string => {
  const { year, quarter } = getCurrentQuarter();
  return `${alias.toLowerCase()}${ROOT_FOLDER_SUFFIX}-q${quarter}-${year}`;
};

/**
 * One "create these folders under this parent" instruction. `parentLogin === null`
 * means the children are created directly under the `Supplemental Context` folder
 * (the top level of the org tree); otherwise the children are created under the
 * folder that was itself created for `parentLogin` at an earlier BFS level.
 */
export interface ParentGroup {
  parentLogin: string | null;
  childLogins: string[];
}

/**
 * The recursive `Supplemental Context` subtree, expressed as breadth-first levels.
 * Every group in `levels[k]` has its parent folder created by `levels[k-1]` (or,
 * for `levels[0]`, by the `Supplemental Context` folder itself), so an orchestrator
 * can create each level in order and always know its parent's real folder id.
 * `personCount` is the total number of person folders across all levels (used to
 * size a progress bar).
 */
export interface SupplementalPlan {
  levels: ParentGroup[][];
  personCount: number;
}

/**
 * Returns the logins not yet in `visited`, marking each as visited as it is taken.
 * Deduplicates the BFS against cycles/repeats in the PAPI data so a person folder is
 * planned exactly once. Mutates `visited`.
 */
const takeUnvisited = (logins: string[], visited: Set<string>): string[] => {
  const fresh: string[] = [];
  for (const login of logins) {
    if (visited.has(login)) continue;
    visited.add(login);
    fresh.push(login);
  }
  return fresh;
};

/**
 * Turns the loaded org (a flat list of employees, each carrying `managerLogin`)
 * into an ordered, breadth-first plan of person folders to create under the
 * `Supplemental Context` folder. The nesting mirrors the manager -> reports hierarchy
 * used by the PDF ZIP export (via the shared {@link buildFolderStructure}), so the
 * WorkDocs tree and the PDF tree have identical shapes.
 *
 * Level 0 holds the leader's own reports (plus any employees with no known
 * manager, bucketed at the top level). Each subsequent level holds the reports of
 * managers placed on the previous level. A `visited` set guards against cycles or
 * duplicates in the PAPI data so this always terminates.
 *
 * Pure and React-free: no network, no side effects.
 */
export function buildSupplementalPlan(employees: OrgChartEmployee[], leaderLogin: string): SupplementalPlan {
  // managerLogin -> [direct report logins]. Reports whose manager is the leader are
  // keyed under `leaderLogin`; reports with no manager are keyed under '' (orphans).
  const adjacency = buildFolderStructure(employees, leaderLogin);

  const levels: ParentGroup[][] = [];
  const visited = new Set<string>();

  // Level 0: the leader's direct reports and any orphans, all directly under the
  // `Supplemental Context` folder (parentLogin === null).
  const topLevelLogins = [...(adjacency.get(leaderLogin) ?? []), ...(adjacency.get('') ?? [])];
  const initialGroup: ParentGroup = { parentLogin: null, childLogins: takeUnvisited(topLevelLogins, visited) };

  let personCount = 0;

  if (initialGroup.childLogins.length > 0) {
    levels.push([initialGroup]);
    personCount += initialGroup.childLogins.length;
  }

  // Expand breadth-first: for every login placed on the current level, emit a group
  // for its own reports (if any) on the next level. Stops when a level adds nothing.
  let frontier = initialGroup.childLogins;
  while (frontier.length > 0) {
    const nextLevel: ParentGroup[] = [];

    for (const parentLogin of frontier) {
      const reports = adjacency.get(parentLogin);
      if (!reports || reports.length === 0) continue;

      const childLogins = takeUnvisited(reports, visited);
      if (childLogins.length > 0) {
        nextLevel.push({ parentLogin, childLogins });
        personCount += childLogins.length;
      }
    }

    if (nextLevel.length === 0) break;

    levels.push(nextLevel);
    frontier = nextLevel.flatMap((group) => group.childLogins);
  }

  return { levels, personCount };
}

/**
 * Builds a flat (single-level) plan: one person folder per direct report, all created
 * directly under the `Supplemental Context` folder with no nesting. Used for the
 * "Direct Reports" provisioning mode, where only the leader's immediate reports get a
 * folder. Returns the same {@link SupplementalPlan} shape as {@link buildSupplementalPlan}
 * so the provisioning create-loop is identical for both modes.
 *
 * Duplicate logins are removed (order-preserving) so a report is planned exactly once.
 * Pure and React-free: no network, no side effects.
 */
export function buildFlatPlan(directReportLogins: string[]): SupplementalPlan {
  const childLogins = Array.from(new Set(directReportLogins));

  if (childLogins.length === 0) {
    return { levels: [], personCount: 0 };
  }

  const level: ParentGroup[] = [{ parentLogin: null, childLogins }];
  return { levels: [level], personCount: childLogins.length };
}
